import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { navigateRequireAuth } from '../../utils/authRedirect';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { boutiqueItemsAPI } from '../../api/boutiqueItemsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 680px;
  background: white;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  color: white;
`;

const Title = styled.h1`font-size: 1.6rem; font-weight: 800; margin: 0 0 0.25rem;`;
const Subtitle = styled.p`font-size: 0.9rem; opacity: 0.85; margin: 0;`;

const FormArea = styled.div`padding: 2rem;`;
const Group = styled.div`margin-bottom: 1.25rem;`;
const Label = styled.label`
  display: block; font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 0.4rem;
`;
const Input = styled.input`
  width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #d1d5db; border-radius: 0.5rem;
  font-size: 0.95rem; box-sizing: border-box;
  &:focus { outline: none; border-color: #764ba2; }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #d1d5db; border-radius: 0.5rem;
  font-size: 0.95rem; resize: vertical; min-height: 110px; font-family: inherit; box-sizing: border-box;
  &:focus { outline: none; border-color: #764ba2; }
`;
const CharCount = styled.p`font-size: 0.75rem; color: #9ca3af; margin: 0.25rem 0 0; text-align: right;`;

const LogoArea = styled.div`display: flex; align-items: center; gap: 1.25rem;`;
const LogoPreview = styled.div`
  width: 80px; height: 80px; border-radius: 50%; border: 2px dashed #764ba2;
  overflow: hidden; flex-shrink: 0; background: #f8f9fa;
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const UploadBtn = styled.label`
  display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1.1rem;
  background: #f3f0ff; color: #764ba2; border-radius: 0.5rem;
  font-size: 0.875rem; font-weight: 600; cursor: pointer;
  &:hover { background: #e9e3ff; }
`;

const Actions = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 2rem; border-top: 1px solid #f0f0f0; background: #fafafa;
`;
const BtnCancel = styled.button`
  padding: 0.7rem 1.5rem; border: 1.5px solid #d1d5db; border-radius: 0.5rem;
  background: white; color: #374151; font-weight: 600; cursor: pointer;
  &:hover { border-color: #764ba2; color: #764ba2; }
`;
const BtnSave = styled.button`
  padding: 0.7rem 1.75rem; border: none; border-radius: 0.5rem;
  background: linear-gradient(135deg, #667eea, #764ba2); color: white;
  font-weight: 700; font-size: 0.95rem; cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const IMAGE_BASE = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

const EditShopPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector(s => s.auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopId, setShopId] = useState(null);
  const [form, setForm] = useState({ brand_name: '', bio: '', instagram_handle: '', social_link: '', return_policy: '', shipping_policy: '' });
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigateRequireAuth(navigate, location); return; }
    (async () => {
      try {
        const shop = await boutiqueItemsAPI.getMyShop();
        setShopId(shop.id);
        setForm({
          brand_name: shop.brand_name || '',
          bio: shop.bio || '',
          instagram_handle: shop.instagram_handle || '',
          social_link: shop.social_link || '',
          return_policy: shop.return_policy || '',
          shipping_policy: shop.shipping_policy || '',
        });
        if (shop.logo_url) {
          const url = shop.logo_url.startsWith('http') ? shop.logo_url : `${IMAGE_BASE}${shop.logo_url}`;
          setExistingLogoUrl(url);
        }
      } catch {
        toast.error("You don't have a shop yet.");
        navigate('/create-boutique');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, navigate, location]);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleLogoChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`"${file.name}" has an unsupported format. Use JPEG, PNG, GIF, or WebP.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`"${file.name}" is too large (max 10MB).`);
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.brand_name.trim()) { toast.error('Brand name is required.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('brand_name', form.brand_name.trim());
      fd.append('bio', form.bio.trim());
      fd.append('instagram_handle', form.instagram_handle.trim());
      fd.append('social_link', form.social_link.trim());
      fd.append('return_policy', form.return_policy.trim());
      fd.append('shipping_policy', form.shipping_policy.trim());
      if (logoFile) fd.append('logo', logoFile);
      await boutiqueItemsAPI.updateShop(fd);
      toast.success('Shop updated!');
      navigate(`/boutique-shop/${boutiqueItemsAPI.buildShopRef(shopId)}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update shop.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading your shop…" />;

  const displayLogo = logoPreview || existingLogoUrl;

  return (
    <Page>
      <Card>
        <CardHeader>
          <Title>✏️ Edit Your Boutique</Title>
          <Subtitle>Update your brand info and policies anytime.</Subtitle>
        </CardHeader>

        <FormArea>
          <Group>
            <Label>Brand Name *</Label>
            <Input
              type="text"
              value={form.brand_name}
              onChange={e => update('brand_name', e.target.value)}
              maxLength={150}
            />
          </Group>

          <Group>
            <Label>Shop Logo</Label>
            <LogoArea>
              <LogoPreview>
                {displayLogo ? <img src={displayLogo} alt="logo" /> : <span style={{ fontSize: '2rem' }}>🏪</span>}
              </LogoPreview>
              <div>
                <UploadBtn htmlFor="logo-edit">
                  📷 {logoFile ? 'Change logo' : 'Update logo'}
                  <input id="logo-edit" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                </UploadBtn>
              </div>
            </LogoArea>
          </Group>

          <Group>
            <Label>Bio</Label>
            <Textarea
              value={form.bio}
              onChange={e => update('bio', e.target.value)}
              maxLength={600}
              placeholder="Your brand story…"
            />
            <CharCount>{form.bio.length}/600</CharCount>
          </Group>

          <Group>
            <Label>Instagram Handle</Label>
            <Input
              type="text"
              value={form.instagram_handle}
              onChange={e => update('instagram_handle', e.target.value)}
              maxLength={100}
              placeholder="@yourbrand"
            />
          </Group>

          <Group>
            <Label>Other Social / Website</Label>
            <Input
              type="url"
              value={form.social_link}
              onChange={e => update('social_link', e.target.value)}
              maxLength={500}
              placeholder="https://tiktok.com/@yourbrand"
            />
          </Group>

          <Group>
            <Label>Return Policy</Label>
            <Textarea
              value={form.return_policy}
              onChange={e => update('return_policy', e.target.value)}
              maxLength={1000}
              placeholder="Return and exchange terms…"
            />
            <CharCount>{form.return_policy.length}/1000</CharCount>
          </Group>

          <Group>
            <Label>Shipping Policy</Label>
            <Textarea
              value={form.shipping_policy}
              onChange={e => update('shipping_policy', e.target.value)}
              maxLength={1000}
              placeholder="Shipping timelines and details…"
            />
            <CharCount>{form.shipping_policy.length}/1000</CharCount>
          </Group>
        </FormArea>

        <Actions>
          <BtnCancel onClick={() => navigate(-1)}>Cancel</BtnCancel>
          <BtnSave onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Changes'}
          </BtnSave>
        </Actions>
      </Card>
    </Page>
  );
};

export default EditShopPage;
